import React, { useEffect, useMemo, useState } from "react";
import * as Mui from "@mui/material";
import moment from "moment";
import { useSelector } from "react-redux";
import { InfoOutlined } from "@mui/icons-material";
import { GlobalSidePanel } from "components/DesignSystem/GlobalSidePanel";
import { RequestInfoBanner } from "components/DesignSystem/RequestInfoBanner";
import { SelectedEmployeeHeader } from "pages/TeamZone/Approve/SelectedEmployeeHeader";
import { PanelActionFooter } from "components/CustomComponents/DesignSystemButtons";
import { CustomAutoCompleteWithLogo } from "components/CustomComponents/CustomAutoCompleteWithLogo";
import { LoaderWithoutHeight } from "components/Loader";
import { tooltipStyleProps } from "services/constants/Styles";
import { designColors, designType } from "services/constants/designTokens";
import { ComingSoonChip } from "../../Shared/ConfigurationUi";
import { DateField, SelectField, TextField } from "../Shared/fieldInputs";
import { Field, FormSection, panelBodySx } from "../Shared/formUi";
import { formatMoney } from "../Shared/formatters";
import { loadShiftFormOptions } from "../ShiftEarnings/shiftEarningsApi";
import { TextAreaField, required } from "./LoanPanels";
import { PLAN, migratedOutstanding, monthOptions, round2, validateMigratedForm } from "./loansData";

// Pay Inputs > Loans & Advances > Add Migrated Loan: the remaining balance
// of a loan the employee already had (approved) before Cauver. Fields and
// order from the mockup, laid out like the other Pay Inputs panels (section
// cards on the page background, one-line helper under every label). Saved
// through saveMigratedLoan (loansApi.js), i.e. the existing POST
// advance/request/; the backend handles the migration in the background.

const INSTALMENT_COUNTS = Array.from({ length: 60 }, (_, index) => ({ value: String(index + 1), label: String(index + 1) }));

// advance/request/ only takes equal instalments, so the custom schedule is
// shown disabled with a Coming Soon chip.
const PlanRadio = ({ value, onChange, disabled }) => (
  <Mui.RadioGroup value={value} onChange={(event) => onChange(event.target.value)} sx={{ gap: 0.5 }}>
    {[
      { value: PLAN.equal, label: "Enter Remaining Instalments" },
      { value: PLAN.custom, label: "Enter Custom Schedule", comingSoon: true },
    ].map((option) => (
      <Mui.FormControlLabel
        key={option.value}
        value={option.value}
        disabled={disabled || option.comingSoon}
        control={<Mui.Radio size="small" sx={{ py: 0.5, "&.Mui-checked": { color: designColors.tealDark } }} />}
        label={
          <Mui.Typography component="span" sx={designType.tableBodyCell}>
            {option.label}
            {option.comingSoon ? <ComingSoonChip /> : null}
          </Mui.Typography>
        }
        sx={{ m: 0 }}
      />
    ))}
  </Mui.RadioGroup>
);

export const MigratedLoanPanel = ({ open, initial, onClose, onSave, expanded, onToggleExpand, saving }) => {
  const { domain, topbarCompanyId } = useSelector((state) => state.tokenReducer);
  const [employees, setEmployees] = useState([]);
  const [employeesError, setEmployeesError] = useState("");
  const [employeesLoading, setEmployeesLoading] = useState(false);
  const [retry, setRetry] = useState(0);
  const [form, setForm] = useState(initial);
  const [showErrors, setShowErrors] = useState(false);

  // Same employee list as Add Loan / Advance.
  useEffect(() => {
    if (!open) return undefined;
    const controller = new AbortController();
    setEmployeesLoading(true);
    setEmployeesError("");
    loadShiftFormOptions(domain, topbarCompanyId, controller.signal)
      .then((data) => {
        if (controller.signal.aborted) return;
        setEmployees(data.employees);
        setEmployeesError(data.errors.find((text) => text.includes("employees")) || "");
      })
      .catch((error) => { if (!controller.signal.aborted) setEmployeesError(error.message); })
      .finally(() => { if (!controller.signal.aborted) setEmployeesLoading(false); });
    return () => controller.abort();
  }, [open, domain, topbarCompanyId, retry]);

  useEffect(() => {
    if (open) {
      setForm(initial);
      setShowErrors(false);
    }
  }, [open, initial]);

  const errors = useMemo(() => (form ? validateMigratedForm(form) : {}), [form]);
  if (!form) return null;

  const set = (key) => (value) => setForm((current) => ({ ...current, [key]: value }));
  const visibleError = (key) => (showErrors ? errors[key] : undefined);
  const submit = () => {
    if (Object.keys(errors).length) {
      setShowErrors(true);
      return;
    }
    onSave(form);
  };

  // Changing the balance or the count refills an equal instalment amount.
  const setAndRefill = (key) => (value) =>
    setForm((current) => {
      const next = { ...current, [key]: value };
      const balance = migratedOutstanding(next);
      const n = Number(next.remaining_instalments);
      if (balance > 0 && Number.isInteger(n) && n > 0) next.instalment_amount = String(round2(balance / n));
      return next;
    });

  const outstanding = migratedOutstanding(form);
  const count = Number(form.remaining_instalments) || 0;
  const instalment = Number(form.instalment_amount) || 0;
  const scheduleTotal = round2(count * instalment);
  const matches = count > 0 && instalment > 0 && Math.abs(scheduleTotal - outstanding) < 0.01 * count;
  const selected = employees.find((option) => option.value === form.employee_code) ?? null;

  return (
    <GlobalSidePanel
      open={open}
      onClose={saving ? undefined : onClose}
      expanded={expanded}
      onToggleExpand={onToggleExpand}
      title="Add Migrated Loan"
      subtitle="Record a loan issued before Cauver adoption."
      bodySx={panelBodySx}
      footer={
        <PanelActionFooter
          onCancel={saving ? undefined : onClose}
          primaryLabel="Save"
          primaryProps={{ onClick: submit, loading: saving, disabled: saving || employeesLoading }}
        />
      }
    >
      {/* Employees load first; the form shows once they are in (same as
          the other Pay Inputs panels). */}
      {employeesLoading ? <LoaderWithoutHeight info="Loading..." /> : (
      <Mui.Stack spacing={2}>
        {employeesError ? (
          <RequestInfoBanner
            tone="danger"
            title="Employees could not be loaded"
            body={<>{employeesError} <Mui.Link component="button" onClick={() => setRetry((value) => value + 1)}>Try again</Mui.Link></>}
          />
        ) : null}
        <RequestInfoBanner
          title="Opening balance"
          body="This records the remaining balance of an existing loan. It will not trigger a new disbursement."
        />

        <FormSection title="Employee">
          <Mui.Box sx={{ gridColumn: "1 / -1" }}>
            <Field label={required("Employee")} helper="Employee the loan belongs to. Search by name or employee code." error={visibleError("employee_code")}>
              <CustomAutoCompleteWithLogo
                dataList={employees}
                selectedValue={selected}
                updateDataFunction={(event, option) =>
                  setForm((current) => ({ ...current, employee_code: option?.value ?? "", user_id: option?.user_id ?? "" }))
                }
                placeHolder="Select employee"
                textBoxError={Boolean(visibleError("employee_code"))}
                disableAction={saving}
                showAvathar={!selected}
              />
            </Field>
          </Mui.Box>
        </FormSection>
        {selected ? <SelectedEmployeeHeader selectedUser={selected} /> : null}

        <FormSection title="Loan Balance" info="What was given and repaid before Cauver.">
          <Field label={required("Original Loan Amount (₹)")} helper="Amount given before Cauver." error={visibleError("original_amount")}>
            <TextField type="number" value={form.original_amount} disabled={saving} error={visibleError("original_amount")} onChange={setAndRefill("original_amount")} />
          </Field>
          <Field
            label={
              <Mui.Stack component="span" direction="row" spacing={0.5} alignItems="center">
                <span>{required("Amount Already Repaid (₹)")}</span>
                <Mui.Tooltip title="Total repaid before Cauver adoption. Enter 0 if nothing was repaid." arrow slotProps={tooltipStyleProps}>
                  <InfoOutlined sx={{ fontSize: 16, color: designColors.slate400 }} />
                </Mui.Tooltip>
              </Mui.Stack>
            }
            helper="Repaid before Cauver."
            error={visibleError("repaid_before")}
          >
            <TextField type="number" value={form.repaid_before} disabled={saving} error={visibleError("repaid_before")} onChange={setAndRefill("repaid_before")} />
          </Field>
          <Field label="Outstanding Balance (₹)" helper="Original − already repaid (auto).">
            <TextField value={formatMoney(outstanding)} disabled onChange={() => {}} />
          </Field>
          <Field label={required("Loan Issue Date")} helper="Date the loan was first given." error={visibleError("issue_date")}>
            <DateField value={form.issue_date} maxDate={moment().format("YYYY-MM-DD")} disabled={saving} error={visibleError("issue_date")} onChange={set("issue_date")} />
          </Field>
        </FormSection>

        <FormSection title="Recovery" info="How the outstanding balance is recovered in payroll.">
          <Field label={required("Repayment Plan")} helper="Equal monthly instalments. A custom schedule isn't available yet." wide>
            <PlanRadio value={form.repayment_plan} disabled={saving} onChange={set("repayment_plan")} />
          </Field>
          <>
              {/* Short label: a wrapped label pushed this input below its neighbour. */}
              <Field label={required("Remaining Instalments")} helper="Payrolls left to recover." error={visibleError("remaining_instalments")}>
                <SelectField value={form.remaining_instalments} options={INSTALMENT_COUNTS} disabled={saving} error={visibleError("remaining_instalments")} placeholder="Select" onChange={setAndRefill("remaining_instalments")} />
              </Field>
              <Field label={required("Instalment Amount (₹)")} helper="Deducted in each payroll." error={visibleError("instalment_amount")}>
                <TextField type="number" value={form.instalment_amount} disabled={saving} error={visibleError("instalment_amount")} onChange={set("instalment_amount")} />
              </Field>
              {count > 0 && instalment > 0 ? (
                <Mui.Box sx={{ gridColumn: "1 / -1" }}>
                  <RequestInfoBanner
                    tone={matches ? "info" : "warning"}
                    title={`${count} instalment${count === 1 ? "" : "s"} × ${formatMoney(instalment)} = ${formatMoney(scheduleTotal)}`}
                    body={matches ? "Matches the outstanding balance." : `Ensure the total matches the outstanding balance (${formatMoney(outstanding)}).`}
                  />
                </Mui.Box>
              ) : null}
              <Field label={required("Recovery Starts From")} helper="First payroll the instalment is deducted in." error={visibleError("start_month")} wide>
                <SelectField value={form.start_month} options={monthOptions()} disabled={saving} error={visibleError("start_month")} placeholder="Select month" onChange={set("start_month")} />
              </Field>
          </>
        </FormSection>

        <FormSection title="Additional Information">
          <Field label="Notes (Optional)" helper="Anything worth keeping, e.g. the previous system's reference." wide>
            <TextAreaField value={form.additional_info} disabled={saving} placeholder="e.g. Car loan taken from previous company." onChange={set("additional_info")} />
          </Field>
        </FormSection>
      </Mui.Stack>
      )}
    </GlobalSidePanel>
  );
};
